import React from "react";

// Font Awesome Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";

/**
 * ListItemData
 * @typedef {Object} ListItemData
 * @property {string} id - required id of the list item.
 * @property {string} class - optional classes applied to the element tag.
 * @property {string} text - required text to show for the list item.
 * @property {Object} icon - custom icon to show, we use the object passed to the custom `<Icon>` component.
 */

/**
 * This component renders the list of items with ids and custom list items icons.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.type - if defined and its value it's "ul" we render list items inside a `<ul>` if not we use `<ol>`.
 * @param {string} props.class - optional classes for the `<ul>` or `<ol>` container.
 * @param {boolean} props.greenCheckmark - if true and the list items will have a green checkmark icon applied above, and any icon specified in the item will be ignored.
 * @param {boolean} props.greenCircleCheckmark - The greenCircleNumber flag is used to set the ordered list class to include "green-circle-number" above, 
 * and any icon specified in the item will be ignored.
 * Conversely you could leave the greenCircleNumber flag as false and explicitly add "green-circle-number" to the listClass,
 * but do not set both the flag and the listClass.
 * @param {boolean} props.isCommercial - is to change the color for the icon like this isCommercial ? "text-info" : "text-success".
 * @param {boolean} props.greenCircleNumber - if true shows the numbers of each list item inside a green circle.
 * @param {string} props.accountName - string used as id to group the list items with the same id category.
 * @param {ListItemData[]} props.items - used to render the list items except when type is different than "ul" and greenCircleNumber is "true".
 * @param {string} props.idList - id for each list item used when type is equal to "ul", greenCheckmark is "true" and accountName is not defined.
 * @param {Object[]} props.list - used to render the list items just when type is different than "ul" and greenCircleNumber is "true".
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const List = ({
  type = "ul",
  class: className = "",
  greenCheckmark = false,
  greenCircleCheckmark = false,
  isCommercial = false,
  greenCircleNumber = false,
  accountName = "",
  items = [],
  idList = "",
  list = []
}) => {
  return (
    <>
      {type === "ul" ? (
        <>
          {greenCheckmark && (
            <ul className={`${className} fa-ul`}>
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className={item.class}
                  id={
                    accountName
                      ? `${accountName}-${item.id}`
                      : idList
                      ? `list-${idList}-item-${index + 1}`
                      : `item-${index + 1}`
                  }
                >
                  <span className="fa-li">
                    <FontAwesomeIcon
                      className={`${isCommercial ? "text-info" : "text-success"} mr-2`}
                      icon={["fas", "check"]}
                    />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: item.text }}></span>
                </li>
              ))}
            </ul>
          )}
          {greenCircleCheckmark && (
            <ul className={`${className} fa-ul`}>
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className={item.class}
                  id={accountName ? `${accountName}-${item.id}` : `item-${index + 1}`}
                >
                  <span className="fa-li" style={{ fontSize: "1.2em", lineHeight: "1.25em" }}>
                    <FontAwesomeIcon
                      className={`${isCommercial ? "text-info" : "text-success"} mr-2`}
                      icon={["fas", "check-circle"]}
                    />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: item.text }}></span>
                </li>
              ))}
            </ul>
          )}
          {!greenCheckmark && !greenCircleCheckmark && (
            <ul id={accountName ? accountName : ""} className={className}>
              {items.map((item, index) => (
                <li
                  key={index}
                  className={item.class}
                  id={accountName ? `${accountName}-${item.id}` : `item-${index + 1}`}
                >
                  {item.icon && <FontAwesomeIcon className={`${item.icon.class} mr-2`} icon={item.icon} />}
                  {item.text && <span dangerouslySetInnerHTML={{ __html: item.text }} />}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : greenCircleNumber ? (
        <ol className={`${className} green-circle-number`}>
          {list.map((item, index) => (
            <li key={index} className={item.class} id={accountName ? `${accountName}-${item.id}` : `item-${index + 1}`}>
              {item.text}
            </li>
          ))}
        </ol>
      ) : (
        <ol className={className}>
          {items.map((item, index) => (
            <li key={index} className={item.class} id={accountName ? `${accountName}-${item.id}` : `item-${index + 1}`}>
              {item.icon && <FontAwesomeIcon className={`${item.icon.class} mr-2`} icon={item.icon} />}
              {item.text && <span dangerouslySetInnerHTML={{ __html: item.text }} />}
            </li>
          ))}
        </ol>
      )}
    </>
  );
};
export default List;

List.propTypes = {
  type: PropTypes.string,
  class: PropTypes.string,
  greenCheckmark: PropTypes.bool,
  greenCircleCheckmark: PropTypes.bool,
  isCommercial: PropTypes.bool,
  greenCircleNumber: PropTypes.bool,
  accountName: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      class: PropTypes.string,
      text: PropTypes.string.isRequired,
      icon: PropTypes.object
    })
  )
};
